import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';

const SkinScannerTab = ({ user, loading: parentLoading, result, setResult }) => {
  const [skinImage, setSkinImage] = useState(null);
  const [skinImagePreview, setSkinImagePreview] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [loading, setLoading] = useState(false);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      setCameraStream(stream);
      setShowCamera(true);
      setTimeout(() => {
        const videoElement = document.getElementById('camera-preview');
        if (videoElement) videoElement.srcObject = stream;
      }, 100);
    } catch (error) {
      toast.error('Failed to access camera: ' + error.message);
    }
  };

  const capturePhoto = () => {
    const videoElement = document.getElementById('camera-preview');
    if (!videoElement || !videoElement.videoWidth) { toast.error('Camera not ready'); return; }
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    canvas.getContext('2d').drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('Failed to capture photo'); return; }
      setSkinImage(new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' }));
      setSkinImagePreview(canvas.toDataURL('image/jpeg', 0.95));
      stopCamera();
      toast.success('Photo captured!');
    }, 'image/jpeg', 0.95);
  };

  const stopCamera = () => {
    if (cameraStream) { cameraStream.getTracks().forEach(track => track.stop()); setCameraStream(null); }
    setShowCamera(false);
  };

  const handleSkinScan = async () => {
    if (!skinImage) { toast.error('Please upload or capture an image first'); return; }
    setLoading(true);
    setResult(null);
    try {
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(skinImage);
      });
      const response = await axios.post(`${API}/skin-scanner/analyze`, {
        image_data: base64Data, body_part: 'general', symptoms: 'general skin check', duration: 'recent'
      }, { timeout: 60000 });
      setResult({ type: 'skin', data: response.data });
      toast.success('Skin analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze skin: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-6" data-testid="skin-scanner-tab">
      <h2 className="text-2xl font-bold text-cyan-600 mb-6">AI Skin Scanner</h2>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold mb-4">Upload or Capture Image</h3>
          {showCamera ? (
            <div>
              <video id="camera-preview" autoPlay playsInline className="w-full rounded-lg bg-black mb-4" style={{ maxHeight: '300px' }} />
              <div className="flex gap-2">
                <Button onClick={capturePhoto} className="flex-1 bg-green-600 hover:bg-green-700">Capture Photo</Button>
                <Button onClick={stopCamera} variant="outline">Cancel</Button>
              </div>
            </div>
          ) : skinImagePreview ? (
            <div>
              <img src={skinImagePreview} alt="Skin" className="w-full rounded-lg border-2 mb-4" style={{ maxHeight: '300px', objectFit: 'contain' }} />
              <Button onClick={() => { setSkinImage(null); setSkinImagePreview(''); }} variant="outline" className="w-full mb-2">Choose Different Image</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) { setSkinImage(file); const reader = new FileReader(); reader.onload = (ev) => setSkinImagePreview(ev.target.result); reader.readAsDataURL(file); }
                }} className="hidden" id="skin-upload" />
                <label htmlFor="skin-upload" className="cursor-pointer text-gray-500"><p className="text-lg mb-2">Click to upload image</p><p className="text-sm">JPG, PNG up to 10MB</p></label>
              </div>
              <Button onClick={startCamera} variant="outline" className="w-full">Use Camera</Button>
            </div>
          )}
        </div>
        <div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800"><strong>Disclaimer:</strong> This AI analysis is for informational purposes only. Always consult a qualified dermatologist.</p>
          </div>
          <Button onClick={handleSkinScan} disabled={loading || !skinImage} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="analyze-skin-btn">
            {loading ? 'Analyzing...' : 'Analyze Skin Condition'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default SkinScannerTab;
